import "./tournament-sponsorship-tier-manage-sponsors.scss";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  getSponsorshipTierSponsors,
  getTournamentSponsors,
  getTournamentSponsorshipTier,
  selectCurrentSponsorshipTier,
  selectCurrentSponsorshipTierSponsors,
  selectLoading,
  selectSponsorshipTierList,
  selectSponsorshipTierNotifications,
  selectTournamentSponsors,
  setCurrentSponsorshipTierSponsors,
  setTournamentSponsors,
} from "../../../reducers/sponsorshipTierSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GSEmptyList,
  GSForm,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faPlus,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import {
  getTierPaidMessage,
  manageSponsorsForm,
  TIER_MANAGEMENT_LIST,
} from "../../../forms/SponsorshipTierForm";
import { getNotificationItemInfo } from "../../../helpers/Converters";
import {
  saveTournamentSponsorOrder,
  selectSponsorNotifications,
  clearNotifications as clearSponsorNotifications,
  saveTournamentSponsor,
} from "../../../reducers/sponsorSlice";
import { selectTiersAvailable } from "../../../reducers/tournamentSlice";

const TournamentSponsorshipTierManageSponsors = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch(
    "/:tournamentID/sponsors/sponsorship-tiers/:sponsorshipTierID/edit/manage-sponsors"
  );
  const [search, setSearch] = useState("");
  const sponsorshipTierNotifications = useSelector(
    selectSponsorshipTierNotifications
  );
  const sponsorNotifications = useSelector(selectSponsorNotifications);
  const currentSponsorshipTier = useSelector(selectCurrentSponsorshipTier);
  const tournamentSponsors = useSelector(selectTournamentSponsors);
  const tiersAvailable = useSelector(selectTiersAvailable);
  const tournamentSponsorshipTiers = useSelector(selectSponsorshipTierList);
  const defaultTier = tournamentSponsorshipTiers?.find?.(
    (t) => t?.sponsorshipTierType === "default"
  );
  const currentSponsorshipTierSponsors = useSelector(
    selectCurrentSponsorshipTierSponsors
  );
  const tierLoading = useSelector(selectLoading);

  const context = useMemo(() => ({}), []);

  useEffect(() => {
    if (
      match?.params?.sponsorshipTierID &&
      match?.params?.sponsorshipTierID !== currentSponsorshipTier?.id
    ) {
      dispatch(getTournamentSponsorshipTier(match?.params?.sponsorshipTierID));
      dispatch(getSponsorshipTierSponsors(match?.params?.sponsorshipTierID));
    }
  }, [dispatch, currentSponsorshipTier?.id, match?.params?.sponsorshipTierID]);

  useEffect(() => {
    dispatch(getTournamentSponsors(match.params.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  const leftNavigation = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (currentSponsorshipTier?.sponsorshipTierType === "technology") {
      leftNavigation();
    }
  }, [currentSponsorshipTier?.sponsorshipTierType, leftNavigation]);

  const getNavigation = () => {
    return {
      title: `Manage Sponsors`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  context.currentSponsorshipTierSponsors = [...currentSponsorshipTierSponsors];
  context.tierLoading = [...tierLoading];
  context.setSearch = setSearch;
  context.search = search;
  context.findTier = (sponsor) => {
    let tier = tournamentSponsorshipTiers?.find(
      (t) => t?.id === sponsor?.sponsorshipTierId
    );
    return tier;
  };

  const availableSponsors = useMemo(() => {
    const inTierIds = new Set(
      currentSponsorshipTierSponsors?.map?.((s) => s?.id)
    );

    return tournamentSponsors
      .filter((s) => s?.sponsorshipType !== "leaderboard")
      .filter((s) => !inTierIds?.has(s.id))
      .filter((s) => s?.sponsorshipTierId !== currentSponsorshipTier?.id)
      .filter((s) => s.name?.toLowerCase?.().includes(search?.toLowerCase?.()))
      .sort((a, b) => {
        const aInDefault = a?.sponsorshipTierId === defaultTier?.id ? 0 : 1;
        const bInDefault = b?.sponsorshipTierId === defaultTier?.id ? 0 : 1;
        return aInDefault - bInDefault;
      });
  }, [
    tournamentSponsors,
    currentSponsorshipTierSponsors,
    currentSponsorshipTier?.id,
    defaultTier?.id,
    search,
  ]);

  context.availableSponsors = availableSponsors;
  context.addSponsorToTier = (sponsor) => {
    dispatch(
      saveTournamentSponsor({
        ...sponsor,
        sortOrder: currentSponsorshipTierSponsors?.length,
        sponsorshipTierId: currentSponsorshipTier?.id,
        sponsorshipTier: currentSponsorshipTier,
      })
    );
    dispatch(
      setCurrentSponsorshipTierSponsors([
        ...currentSponsorshipTierSponsors,
        sponsor,
      ])
    );
  };

  context.removeSponsor = (sponsor) => {
    const updatedSponsor = {
      ...sponsor,
      sponsorshipTierId: defaultTier?.id,
      sponsorshipTier: defaultTier,
    };

    dispatch(saveTournamentSponsor(updatedSponsor));
    let remainingTierSponsors = currentSponsorshipTierSponsors?.filter?.(
      (s) => s?.id !== sponsor?.id
    );
    dispatch(setCurrentSponsorshipTierSponsors(remainingTierSponsors));
    dispatch(
      setTournamentSponsors(
        tournamentSponsors.map((s) =>
          s.id === sponsor.id ? updatedSponsor : s
        )
      )
    );
  };

  context.getEmptyMessage = () => {
    return (
      <GSEmptyList
        title={`${currentSponsorshipTier?.name}`}
        detail="Add sponsors from the list below."
      />
    );
  };

  const clearSearch = () => {
    setSearch("");
  };

  context.getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the sponsor you're looking for..."
        detail="Try changing the search term or adding a new sponsor."
        actions={[
          {
            title: "Clear Search",
            onClick: clearSearch,
          },
          {
            title: "Add Sponsor",
            buttonIcon: faPlus,
            onClick: () => {
              navigate(`../add`);
            },
            type: "black no-wrap",
          },
        ]}
      />
    );
  };

  context.getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Add & Manage Sponsors"
        detail="There are no sponsors available."
        actions={[
          {
            title: "Add Sponsor",
            buttonIcon: faPlus,
            onClick: () => {
              navigate(`../add`);
            },
            type: "black no-wrap",
          },
        ]}
      />
    );
  };

  context.tournamentSponsorsCount = tournamentSponsors?.length;

  context.reorderSponsors = (
    sourceSponsor,
    sourceIndex,
    destSponsor,
    destIndex,
    destListID
  ) => {
    if (destListID !== TIER_MANAGEMENT_LIST) {
      return;
    }
    const tierSponsors = [...currentSponsorshipTierSponsors]?.sort?.(
      (a, b) => a?.sortOrder - b?.sortOrder
    );

    const [movedSponsor] = tierSponsors?.splice?.(sourceIndex, 1);
    tierSponsors?.splice(destIndex, 0, movedSponsor);

    const updatedTierSponsors = tierSponsors?.map?.((sponsor, index) => ({
      ...sponsor,
      sortOrder: index,
    }));

    dispatch(setCurrentSponsorshipTierSponsors(updatedTierSponsors));
    saveOrder(updatedTierSponsors);
  };

  const saveOrder = (sponsorlist) => {
    sponsorlist?.forEach?.(async (sponsor) => {
      dispatch(saveTournamentSponsorOrder(sponsor));
    });
  };

  const getContent = () => {
    if (!tiersAvailable && !tierLoading?.length > 0) {
      return getTierPaidMessage("Manage Sponsors");
    }
    return (
      <GSForm
        formTitle={`${currentSponsorshipTier?.name}`}
        formSections={manageSponsorsForm(context, currentSponsorshipTier)}
      ></GSForm>
    );
  };

  const getDefaultBanner = () => {
    return {
      title: getNotificationItemInfo(
        `Selections will be automatically saved.`,
        faSave
      ),
      state: "grey",
    };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
    dispatch(clearSponsorNotifications());
  };

  let notificationSettings = {
    notifications: [...sponsorshipTierNotifications, ...sponsorNotifications],
    defaultBanner: tiersAvailable ? getDefaultBanner() : null,
    timeoutAction: timeoutAction,
  };

  const [bannerNotifications] = useNotificationBanner(notificationSettings);

  return (
    <tournament-sponsorship-tier-manage-sponsors>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
      ></GSSidePanelPage>
    </tournament-sponsorship-tier-manage-sponsors>
  );
};

export default TournamentSponsorshipTierManageSponsors;
